import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Alert from './Alert';
import { useAppContext } from '../contexts/appContext';
import NavLink from './NavLink';
import Heading from './Heading';
import Separator from './icons/separator';
import { useTranslation, initReactI18next } from "react-i18next";
import { SELECTED_LANGUAGE } from '../constants';
import { updateUserLanguage } from '../api/users';
import { useSession } from '../contexts/sessionContext';
import { useSessionContext } from '../contexts/sessionContext';

import {
  filterNotificationsForViewer,
  getUserCustomsFlags,
} from '../utils/notificationAudience';
import { getPortalNotification } from '../api/users';



export default function BaseLayout({
  logo,
  header,
  menu = [],
  className = '',
  backgroundColor = 'bg-white',
  children,
  isRoot,
  fullScreen = true,
  canEditCatalogs
}) {
  const { currentOrganization, token, userData } = useSessionContext();
  const session = useSession();
  const { i18n, t } = useTranslation();

  const { alerts, removeAlert, clearAlerts } = useAppContext();
  useEffect(() => clearAlerts, []);
  const [activePortalNotifications, setActivePortalNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const languageSelector = localStorage.getItem('languageSelector') !== 'false';
  const logoValidate = localStorage.getItem('logo') !== 'false';
  const initialLanguage = localStorage.getItem(SELECTED_LANGUAGE) || 'en';
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const updateLanguageUser = async (value) => {
    const user = {
      selectedLanguage: value,
    };
    let result = await updateUserLanguage(token, user)

  }

  useEffect(() => {
    if (initialLanguage) {
      updateLang(initialLanguage)
    }
  }, []);

  const updateLang = async (value) => {
    window.localStorage.setItem(SELECTED_LANGUAGE, value);
    setSelectedLanguage(value);
    i18n.changeLanguage(value);
    await updateLanguageUser(value)
  }

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await getPortalNotification(token);
        const notifications = response.notification;
        const flags = getUserCustomsFlags();
        const forViewer = filterNotificationsForViewer(
          notifications,
          currentOrganization?.tenantId,
          flags
        );
        const filteredNotifications = forViewer.filter(
          (notification) =>
            notification.type === "Portal" && notification.isActive === true
        );

        setActivePortalNotifications(filteredNotifications);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    if (currentOrganization && currentOrganization.tenantId) {
      fetchNotifications();
    }
  }, [currentOrganization?.tenantId, token]);


  useEffect(() => {

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === activePortalNotifications.length - 1 ? 0 : prevIndex + 1
      );
    }, 13000);

    return () => {
      clearInterval(interval);
    };
  }, [activePortalNotifications]);

  const containerClasses = [fullScreen && 'overflow-auto', backgroundColor].join(' ');
  return (
    <div className={containerClasses}>
      {fullScreen && (
        <>
          <div className="px-2 lg:px-6 flex h-16 justify-between items-center shadow-md bg-white w-full absolute top-0 z-20">
            <div className="flex items-center">
              {logoValidate && (
                <>
                  <div>
                    <Link href="/">{logo}</Link>
                  </div>
                  <Separator className="mx-0 md:mx-1 w-4 h-4 lg:h-5 lg:w-5 text-gray-300" />
                  <Heading light>{t('Truck')}</Heading>
                </>
              )}
              {!!menu.length && (

                <nav className="ml-3 md:ml-4 lg:ml-8 space-x-2 lg:space-x-4  flex items-center justify-center">
                  {menu.map(({ label, href, icon, hide }, index) => (
                    !hide || isRoot ? (
                      icon ? (
                        <NavLink key={`nav-link-${index}`} href={href}>
                          <div className="inline-flex align-middle">
                            {icon}
                          </div>
                        </NavLink>
                      ) : (
                        label && (
                          <NavLink key={`nav-link-${index}`} href={href}>
                            {label}
                          </NavLink>
                        )
                      )
                    ) : null
                  ))}
                </nav>
              )}
              {languageSelector && (
                <div className="flex items-center ml-5">
                  {initialLanguage === 'es' ? (
                    <img src="/images/mexico-circular.png" width={20}></img>
                  ) : (
                    <img src="/images/usa-circular.png" width={20}></img>
                  )}
                  <select
                    className="py-2 w-full px-2 focus:outline-none border-1 border-gray-500"
                    onChange={async (e) => {
                      if (e?.target?.value) {
                        updateLang(e.target.value.trim())
                      }

                    }}
                    value={selectedLanguage}
                    style={{ height: '3rem', fontSize: '0.75rem', color: '#4D5059', fontWeight: 600 }}
                  >
                    <option value="es">
                      Español
                    </option>
                    <option value="en">
                      English
                    </option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center">{header}</div>
          </div>

          <div className="absolute w-full mt-16 z-50">
            {alerts.map(({ id, type, content }) => (
              <Alert id={id} key={`alert-${id}`} type={type} onClose={() => removeAlert(id)}>
                {content}
              </Alert>
            ))}
          </div>
          {activePortalNotifications && activePortalNotifications.length > 0 && (
            <div className="px-2 lg:px-6 py-2 flex h-12 justify-between items-center shadow-md bg-yellow-400 w-full relative top-16">
              <p>
                {i18n.language === "es" ? (
                  <>
                    {activePortalNotifications[currentIndex]?.spanishMessage}
                    {activePortalNotifications[currentIndex]?.link ? (
                      <a
                        href={activePortalNotifications[currentIndex]?.link}
                        className="text-blue-600 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {activePortalNotifications[currentIndex]?.linkTitle ||
                          activePortalNotifications[currentIndex]?.link}
                      </a>
                    ) : null}
                  </>
                ) : (
                  <>
                    {activePortalNotifications[currentIndex]?.englishMessage}
                    {activePortalNotifications[currentIndex]?.link ? (
                      <a
                        href={activePortalNotifications[currentIndex]?.link}
                        className="text-blue-600 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {activePortalNotifications[currentIndex]?.linkTitle ||
                          activePortalNotifications[currentIndex]?.link}
                      </a>
                    ) : null}
                  </>
                )}
              </p>
            </div>
          )}
        </>
      )}
      <div className={className}>{children}</div>
    </div>
  );
}
